import { type ReactElement } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router';

import { generatePathWithSearch } from '@/utils/url.util';

import { KEYBOARD_BINDINGS, type NavigationBinding } from './bindings';
import { HOTKEY_SCOPES } from './scopes';
import { useSequencePrefixGuard } from './sequenceGuard';

/**
 * Go-to navigation: `g`-then-letter sequences (e.g. `g i` → Insights) that jump
 * between the left-nav sections. Replaces the old Alt+ArrowUp/Down cycle.
 *
 * Each `g>x` sequence gets its OWN `useHotkeys` call (via a one-per-binding
 * child component) so each registration owns an INDEPENDENT sequence buffer.
 *
 * Why per-sequence and not one `useHotkeys(arrayOfSequences, …)` call:
 * react-hotkeys-hook's keydown handler iterates every registered hotkey for a
 * single key event in one synchronous `forEach`, sharing ONE sequence buffer
 * across all of them. With all 12 sequences in a single call, pressing `g`
 * makes the buffer push 'g' for the first sequence (`g>i` → `['g']`, ok), then
 * `g>s` pushes 'g' again → `['g','g']`; since the expected second key `'s'` ≠
 * 'g' the library RESETS the shared buffer to `[]`. This alternates across all
 * 12 sequences and leaves the buffer empty after the `g` keydown, so the
 * following `i` keydown matches nothing and navigate NEVER fires — the feature
 * is dead in real browsers too, not just tests.
 *
 * With per-sequence registration, on `g` each of the 12 listeners pushes 'g'
 * into its OWN buffer (all hold at `['g']`); on the next key (`i`) only the
 * `g>i` listener matches `keys[1]='i'` and navigates, while the others reset
 * their own buffers. Correct.
 *
 * Rendering a list of components that each call `useHotkeys` unconditionally is
 * hooks-safe — the rules-of-hooks constraint is per-component, and each child
 * calls exactly one hook every render.
 *
 * Scope `global`: nav must not fire under an open overlay, so it rides the same
 * `HotkeyScopeSync` suppression the Alt cycle used. `enableOnFormTags: false`
 * keeps the sequences from firing while a form field is focused, and
 * `generatePathWithSearch` preserves the current query params on navigate —
 * identical to the Alt handlers it replaces.
 */
function GoToHotkey({ binding }: { binding: NavigationBinding }): null {
  const navigate = useNavigate();

  useHotkeys(
    binding.keys,
    () => navigate(generatePathWithSearch({ pathname: binding.route })),
    {
      scopes: [HOTKEY_SCOPES.global],
      preventDefault: true,
      enableOnFormTags: false,
    },
    [navigate, binding.route]
  );

  return null;
}

export function GoToNavigation(): ReactElement {
  // Derive the sequence prefixes from the bindings (`g>i`, `g>a`, … → `['g']`)
  // and install ONE shared `document` listener that gates bare-letter actions
  // while a sequence is mid-flight. Done here in the parent — NOT in each
  // per-binding `GoToHotkey` child — so only a single guard listener exists.
  const prefixes = [
    ...new Set(
      KEYBOARD_BINDINGS.navigation.map(binding => binding.keys.split('>')[0])
    ),
  ];
  useSequencePrefixGuard(prefixes);

  return (
    <>
      {KEYBOARD_BINDINGS.navigation.map(binding => (
        <GoToHotkey key={binding.keys} binding={binding} />
      ))}
    </>
  );
}
